<template>
  <form @submit.prevent="handleSubmit" id="vendor_edit_form">

    <!-- Loading -->
    <div v-if="initialLoading" class="text-center py-5">
      <i class="fa fa-spinner fa-spin fs-3 text-primary"></i>
      <div class="text-muted mt-2">Loading vendor details...</div>
    </div>

    <template v-else>

      <!-- Vendor Name -->
      <div class="mb-4 mt-2">
        <label class="form-label form-label-thin">
          Vendor Name <span class="text-danger">*</span>
        </label>

        <input
          type="text"
          class="form-control form-control-thin"
          id="name"
          name="name"
          ref="name"
          v-model="form.name"
          placeholder="Enter vendor name"
        />

        <div v-if="errors.name" class="text-danger small">
          {{ errors.name[0] }}
        </div>
      </div>

      <!-- Description -->
      <div class="mb-4">
        <label class="form-label form-label-thin">Description</label>

        <textarea
          class="form-control form-control-thin"
          id="description"
          name="description"
          ref="description"
          v-model="form.description"
          placeholder="Vendor description"
          rows="2"
        ></textarea>
      </div>

      <!-- Existing Attachments -->
      <div class="mb-4" v-if="form.existing_attachments.length">
        <label class="form-label form-label-thin">Existing Attachments</label>

        <div class="d-flex flex-column gap-2">
          <div
            v-for="attachment in form.existing_attachments"
            :key="attachment.id"
            class="d-flex align-items-center justify-content-between border rounded p-2"
          >
            <div class="d-flex align-items-center gap-2">
              <i
                :class="attachment.file_type === 'image'
                  ? 'fa fa-image text-info'
                  : 'fa fa-file text-secondary'"
              ></i>

              <a
                :href="getFileUrl(attachment.file_path)"
                target="_blank"
                rel="noopener"
                class="text-dark text-decoration-none small"
              >
                {{ attachment.file_name }}
              </a>
            </div>

            <div
              class="text-danger"
              style="cursor: pointer;"
              @click="removeExistingAttachment(attachment)"
            >
              <i class="fa fa-times"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Upload Files -->
      <div class="mb-4">
        <FileUploader
          v-model:files="form.files"
          ref="files"
          :help-text="'Upload additional vendor-related documents or images. Max 10 files.'"
        />
      </div>

      <!-- Contacts -->
      <div class="mb-4">
        <label class="form-label form-label-thin">Contacts</label>

        <div
          class="text-primary mb-2 small"
          style="cursor: pointer;"
          @click="addContact"
        >
          <i class="fa fa-plus"></i> Add Contact
        </div>

        <div
          v-for="(contact, index) in form.contacts"
          :key="contact._key"
          class="contact-card mb-4 p-3 border rounded"
        >
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong class="text-primary">Contact {{ index + 1 }}</strong>

            <div
              v-if="form.contacts.length > 1"
              class="text-danger"
              style="cursor: pointer;"
              @click="removeContact(index)"
            >
              <i class="fa fa-times"></i> Remove
            </div>
          </div>

          <div class="row">

            <!-- Name -->
            <div class="col-md-6 mb-2">
              <input
                type="text"
                class="form-control form-control-thin"
                :id="`contact_name_${index}`"
                :name="`contacts[${index}][name]`"
                :ref="`contact_name_${index}`"
                v-model="contact.name"
                placeholder="Contact Name *"
              />

              <div
                v-if="errors['contacts.' + index + '.name']"
                class="text-danger small"
              >
                {{ errors['contacts.' + index + '.name'][0] }}
              </div>
            </div>

            <!-- Email -->
            <div class="col-md-6 mb-2">
              <input
                type="email"
                class="form-control form-control-thin"
                :id="`contact_email_${index}`"
                :name="`contacts[${index}][email]`"
                :ref="`contact_email_${index}`"
                v-model="contact.email"
                placeholder="Email"
              />
            </div>

            <!-- Phone -->
            <div class="col-md-6 mb-2">
              <input
                type="text"
                class="form-control form-control-thin"
                :id="`contact_phone_${index}`"
                :name="`contacts[${index}][phone]`"
                :ref="`contact_phone_${index}`"
                v-model="contact.phone"
                placeholder="Phone"
              />
            </div>

            <!-- Role -->
            <div class="col-md-6 mb-2">
              <input
                type="text"
                class="form-control form-control-thin"
                :id="`contact_role_${index}`"
                :name="`contacts[${index}][role]`"
                :ref="`contact_role_${index}`"
                v-model="contact.role"
                placeholder="Role"
              />
            </div>

          </div>
        </div>

        <div v-if="errors.contacts" class="text-danger small">
          {{ errors.contacts }}
        </div>
      </div>

      <!-- Vendor Type -->
      <div class="mb-4">
        <label class="form-label form-label-thin">Vendor Type</label>

        <MultiSelect
          v-model="form.vendor_types"
          :options="vendorTypeOptions"
          option-value="value"
          option-label="label"
          placeholder="Select vendor types..."
          multiple
          id="vendor_types"
          name="vendor_types"
          ref="vendor_types"
        />
      </div>

      <!-- Parts -->
      <div class="mb-4">
        <label class="form-label form-label-thin">Associated Parts</label>

        <MultiSelect
          v-model="form.asset_ids"
          :options="parts"
          option-value="id"
          option-label="name"
          placeholder="Select parts..."
          multiple
          id="asset_ids"
          name="asset_ids"
          ref="asset_ids"
        />
      </div>

      <!-- Locations -->
      <div class="mb-4">
        <label class="form-label form-label-thin">Associated Locations</label>

        <MultiSelect
          v-model="form.location_ids"
          :options="locations"
          option-value="id"
          option-label="name"
          placeholder="Select locations..."
          multiple
          id="location_ids"
          name="location_ids"
          ref="location_ids"
        />
      </div>

      <!-- Footer -->
      <div class="d-flex justify-content-end gap-2">
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary hover-white"
          @click="close"
        >
          <i class="fa fa-times"></i> Cancel
        </button>

        <button
          type="submit"
          class="btn btn-primary py-1 px-2 text-light hover-white btn-sm form-submit-btn"
          :disabled="submitting"
        >
          <i class="fa-solid fa-check fs-12"></i>
          {{ submitting ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>

    </template>
  </form>
</template>
<script>
import FileUploader from '@/components/FileUploader.vue';
import { form_api_submit } from '@/utils/FormSubmit'
import MultiSelect from '@/components/MultiSelect.vue'

export default {
  name: 'EditVendor',
  props: {
    // The vendor's primary key (DB id), e.g. pass user.id from the list row
    vendorId: {
      type: [String, Number],
      required: true
    }
  },
  emits: ['updated', 'close'],
  components: {
    MultiSelect,
    FileUploader
  },

  data() {
    return {
      form: {
        name: '',
        description: '',
        files: [],               // newly added files to upload
        existing_attachments: [],// attachments already saved on the vendor
        vendor_types: [],
        asset_ids: [],
        location_ids: [],
        contacts: [
          {
            id: null,
            name: '',
            email: '',
            phone: '',
            role: '',
            _key: cryptoRandomKey()
          }
        ]
      },
      removedAttachmentIds: [],
      removedContactIds: [],
      errors: {},
      submitting: false,
      initialLoading: true,
      // Reactive collections for dropdowns
      assets: [],
      parts: [],
      locations: [],
      vendorTypeOptions: [
        { value: 'manufacturer', label: 'Manufacturer' },
        { value: 'distributor', label: 'Distributor' },
        { value: 'supplier', label: 'Supplier' },
        { value: 'contractor', label: 'Contractor' }
      ],
      loading: false
    }
  },

  async mounted() {
    this.initialLoading = true
    // Fetch dropdown data and the vendor's existing details in parallel
    await Promise.all([
      this.fetchAssets(),
      this.fetchLocations(),
      this.fetchParts(),
      this.fetchVendorDetails()
    ])
    this.initialLoading = false
  },

  methods: {
    addContact() {
      this.form.contacts.push({
        id: null,
        name: '',
        email: '',
        phone: '',
        role: '',
        _key: cryptoRandomKey()
      })
      this.$nextTick(() => {
        const contactCards = document.querySelectorAll('.contact-card');
        if (contactCards.length) {
          contactCards[contactCards.length - 1].scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      });
    },

    removeContact(index) {
      const removed = this.form.contacts[index]
      if (removed && removed.id) {
        this.removedContactIds.push(removed.id)
      }
      this.form.contacts.splice(index, 1)
      // Clear errors for removed contact
      Object.keys(this.errors).forEach(key => {
        if (key.startsWith(`contacts.${index}`)) {
          delete this.errors[key]
        }
      })
    },

    removeExistingAttachment(attachment) {
      this.removedAttachmentIds.push(attachment.id)
      this.form.existing_attachments = this.form.existing_attachments.filter(a => a.id !== attachment.id)
    },

    // Best-effort URL builder for previewing/downloading an already-saved attachment.
    // Adjust the prefix below to match how your app serves stored files (e.g. an
    // asset()/Storage::url() equivalent, a CDN base path, etc.)
    getFileUrl(filePath) {
      if (!filePath) return '#'
      if (/^https?:\/\//i.test(filePath)) return filePath
      return `/storage/${filePath}`
    },

    async handleSubmit() {
      this.submitting = true
      this.errors = {}

      try {
        // 1. Clean contacts
        const contacts = this.form.contacts.filter(c => c.name && c.name.trim())

        if (!contacts.length) {
          this.errors.contacts = ['At least one contact with name is required']
          return
        }

        // 2. Build FormData
        const formData = new FormData()

        formData.append('name', this.form.name || '')
        formData.append('description', this.form.description || '')

        // 3. Selects
        const vendorTypes = Array.isArray(this.form.vendor_types) ? this.form.vendor_types : []
        const assetIds = Array.isArray(this.form.asset_ids) ? this.form.asset_ids : []
        const locationIds = Array.isArray(this.form.location_ids) ? this.form.location_ids : []

        vendorTypes.forEach(v => {
          if (v !== null && v !== '') formData.append('vendor_types[]', v)
        })
        assetIds.forEach(a => {
          if (a !== null && a !== '') formData.append('asset_ids[]', a)
        })
        locationIds.forEach(l => {
          if (l !== null && l !== '') formData.append('location_ids[]', l)
        })

        // 4. Contacts (include id so backend can update vs. insert)
        contacts.forEach((contact, index) => {
          if (contact.id) formData.append(`contacts[${index}][id]`, contact.id)
          formData.append(`contacts[${index}][name]`, contact.name)
          formData.append(`contacts[${index}][email]`, contact.email || '')
          formData.append(`contacts[${index}][phone]`, contact.phone || '')
          formData.append(`contacts[${index}][role]`, contact.role || '')
        })

        // 5. Contacts removed by the user
        this.removedContactIds.forEach(id => {
          formData.append('removed_contact_ids[]', id)
        })

        // 6. Newly added files
        const files = Array.isArray(this.form.files) ? this.form.files : []
        files.forEach(file => {
          formData.append('files[]', file)
        })

        // 7. Attachments removed by the user
        this.removedAttachmentIds.forEach(id => {
          formData.append('removed_attachment_ids[]', id)
        })

        // 8. Submit
        const result = await form_api_submit({
          url: `vendor/update/${this.vendorId}`,
          method: 'POST',
          data: formData,
          curr_form_id: '#vendor_edit_form',
          take_form_data: false
        })

        if (result.success) {
          this.$emit('updated', result.data?.vendor)
          this.close()
        } else if (result.errors) {
          this.errors = result.errors
        }

      } finally {
        this.submitting = false
      }
    },

    close() {
      this.$emit('close')
    },

    async fetchVendorDetails() {
      const result = await form_api_submit({
        url: `vendor/edit/${this.vendorId}`,
        method: 'GET',
        data: {},
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false
      })

      if (result.success) {
        const vendor = result.data?.vendor || result.data || {}

        this.form.name = vendor.first_name || vendor.name || ''
        this.form.description = vendor.get_profile?.description ?? vendor.description ?? ''

        // vendor_types may come back as a JSON string (json_encode'd) or already an array
        let vendorTypes = vendor.get_profile?.vendor_types ?? vendor.vendor_types ?? []
        if (typeof vendorTypes === 'string') {
          try { vendorTypes = JSON.parse(vendorTypes) } catch (e) { vendorTypes = [] }
        }
        this.form.vendor_types = Array.isArray(vendorTypes) ? vendorTypes : []

        this.form.asset_ids = Array.isArray(vendor.asset_ids) ? vendor.asset_ids : []
        this.form.location_ids = Array.isArray(vendor.location_ids) ? vendor.location_ids : []

        const contacts = Array.isArray(vendor.contacts) ? vendor.contacts : []
        this.form.contacts = contacts.length
          ? contacts.map(c => ({
              id: c.id ?? null,
              name: c.name || '',
              email: c.email || '',
              phone: c.phone || '',
              role: c.role || '',
              _key: cryptoRandomKey()
            }))
          : [{ id: null, name: '', email: '', phone: '', role: '', _key: cryptoRandomKey() }]

        this.form.existing_attachments = Array.isArray(vendor.attachments) ? vendor.attachments : []
      }
    },

    async fetchAssets() {
      this.loading = true
      const result = await form_api_submit({
        url: 'asset/dropdown',
        method: 'GET',
        data: {},
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false
      })
      if (result.success) {
        this.assets = result.data.list || result.data || []
      }
      this.loading = false
    },

    async fetchLocations() {
      this.loading = true
      const result = await form_api_submit({
        url: 'locations/get-locations',
        method: 'GET',
        data: {},
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false
      })
      if (result.success) {
        this.locations = result.data || []
      }
      this.loading = false
    },

    async fetchParts() {
      this.loading = true
      const result = await form_api_submit({
        url: 'parts/dropdown',
        method: 'GET',
        data: {},
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false
      })
      if (result.success) {
        this.parts = result.data.list || result.data || []
      }
      this.loading = false
    },
  }
}

// Small helper for stable :key values on dynamically added/removed contact rows
function cryptoRandomKey() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}
</script>

<style scoped>
.contact-card {
  border-radius: 0.375rem;
  transition: all 0.2s ease-in-out;
}

.row {
  display: flex;
  flex-wrap: wrap;
  margin: -0.5rem;
}

.col-md-6 {
  flex: 0 0 50%;
  padding: 0.5rem;
}

@media (max-width: 768px) {
  .col-md-6 {
    flex: 0 0 100%;
  }
}
</style>